import { RiCheckLine, RiFileCopyLine, RiUploadLine } from 'react-icons/ri';
import { useRef, useState, type ChangeEvent, type JSX } from 'react';
import {
  createCuration,
  type CreateCurationPayload,
} from '@/services/slaydAPI/curations';
import {
  createProduct,
  type ProductDataForPost,
} from '@/services/slaydAPI/products';
import { parseCSV } from '@/utils/csvUtils';
import styles from './InternalCreateCurationPage.module.css';

interface ProductRow {
  [key: string]: string | undefined;
}

interface CurationRow {
  [key: string]: string | undefined;
}

const STORAGE_KEY = 'slayd_pending_product_ids';

// Helper functions for localStorage
const saveProductIdsToStorage = (
  productIdMap: Record<string, string>
): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(productIdMap));
  } catch (error) {
    console.error('Failed to save product IDs to storage:', error);
  }
};

const loadProductIdsFromStorage = (): Record<string, string> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to load product IDs from storage:', error);
    return null;
  }
};

const clearProductIdsFromStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear product IDs from storage:', error);
  }
};

const InternalCreateCurationPage = (): JSX.Element => {
  const productFileInputRef = useRef<HTMLInputElement>(null);
  const curationFileInputRef = useRef<HTMLInputElement>(null);
  const [productFile, setProductFile] = useState<File | null>(null);
  const [curationFile, setCurationFile] = useState<File | null>(null);
  const [productUrl, setProductUrl] = useState<string>('');
  const [curationUrl, setCurationUrl] = useState<string>('');
  const [productData, setProductData] = useState<ProductRow[]>([]);
  const [curationData, setCurationData] = useState<CurationRow[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isLoadingUrl, setIsLoadingUrl] = useState<boolean>(false);
  const [progress, setProgress] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [createdCurationId, setCreatedCurationId] = useState<number | null>(
    null
  );
  const [copied, setCopied] = useState<boolean>(false);

  const handleProductFileChange = async (
    event: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (!event.target.files || !event.target.files.length) return;

    const file = event.target.files[0];
    setProductFile(file);
    setProductUrl('');
    setError('');

    try {
      const text = await file.text();
      const parsed = parseCSV(text);
      setProductData(parsed);

      if (parsed.length > 0) {
        const linkColumn = findLinkColumn(parsed[0]);
        const validCount = linkColumn
          ? parsed.filter((row: ProductRow) => {
              const link = row[linkColumn]?.trim();
              return link && link.startsWith('http');
            }).length
          : parsed.length;
        setProgress(
          `Loaded ${validCount} valid rows from product CSV (${parsed.length} total rows parsed)`
        );
      } else {
        setProgress(`Loaded ${parsed.length} rows from product CSV`);
      }
    } catch (err) {
      setError('Failed to parse product CSV file');
      console.error(err);
    }
  };

  const handleCurationFileChange = async (
    event: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (!event.target.files || !event.target.files.length) return;

    const file = event.target.files[0];
    setCurationFile(file);
    setCurationUrl('');
    setError('');

    try {
      const text = await file.text();
      const parsed = parseCSV(text);
      setCurationData(parsed);
      setProgress(`Loaded ${parsed.length} rows from curation CSV`);
    } catch (err) {
      setError('Failed to parse curation CSV file');
      console.error(err);
    }
  };

  const convertGoogleSheetsToCsvUrl = (url: string): string => {
    try {
      const sheetIdMatch = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
      const gidMatch = url.match(/[#&]gid=(\d+)/);

      if (sheetIdMatch) {
        const sheetId = sheetIdMatch[1];
        const gid = gidMatch ? gidMatch[1] : '0';
        return `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${gid}`;
      }
      return url;
    } catch {
      return url;
    }
  };

  const fetchCsvFromUrl = async (url: string): Promise<string> => {
    const csvUrl = convertGoogleSheetsToCsvUrl(url);
    const response = await fetch(csvUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.statusText}`);
    }

    return await response.text();
  };

  const handleProductUrlLoad = async (): Promise<void> => {
    if (!productUrl.trim()) {
      setError('Please enter a URL');
      return;
    }

    setIsLoadingUrl(true);
    setError('');
    setProductFile(null);

    try {
      const csvText = await fetchCsvFromUrl(productUrl);
      const parsed = parseCSV(csvText);
      setProductData(parsed);

      if (parsed.length > 0) {
        const linkColumn = findLinkColumn(parsed[0]);
        const validCount = linkColumn
          ? parsed.filter((row: ProductRow) => {
              const link = row[linkColumn]?.trim();
              return link && link.startsWith('http');
            }).length
          : parsed.length;
        setProgress(
          `Loaded ${validCount} valid rows from product CSV URL (${parsed.length} total rows parsed)`
        );
      } else {
        setProgress(`Loaded ${parsed.length} rows from product CSV URL`);
      }
    } catch (err) {
      setError(
        'Failed to load CSV from URL. Please check the link is accessible.'
      );
      console.error(err);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const handleCurationUrlLoad = async (): Promise<void> => {
    if (!curationUrl.trim()) {
      setError('Please enter a URL');
      return;
    }

    setIsLoadingUrl(true);
    setError('');
    setCurationFile(null);

    try {
      const csvText = await fetchCsvFromUrl(curationUrl);
      const parsed = parseCSV(csvText);
      setCurationData(parsed);
      setProgress(`Loaded ${parsed.length} rows from curation CSV URL`);
    } catch (err) {
      setError(
        'Failed to load CSV from URL. Please check the link is accessible.'
      );
      console.error(err);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const handleProcess = async (): Promise<void> => {
    if (productData.length === 0 || curationData.length === 0) {
      setError('Please load both CSV files (via upload or URL)');
      return;
    }

    if (productData.length === 0 || curationData.length === 0) {
      setError('Please ensure both CSV files have data');
      return;
    }

    setIsProcessing(true);
    setError('');
    setCreatedCurationId(null);
    setCopied(false);
    setProgress('Processing...');

    try {
      const linkColumn = findLinkColumn(productData[0]);

      if (!linkColumn) {
        throw new Error(
          'Could not find product link column. Please ensure your CSV has a column containing product links.'
        );
      }

      const productDataArray: ProductDataForPost[] = [];

      productData.forEach((row: ProductRow) => {
        const link = row[linkColumn]?.trim();
        if (link && link.startsWith('http')) {
          const productDataForPost: ProductDataForPost = {
            product_link: link,
          };

          Object.keys(row).forEach((key) => {
            const value = row[key]?.trim();
            if (value && key !== linkColumn) {
              const apiFieldName = mapColumnToApiField(key);
              if (apiFieldName) {
                productDataForPost[apiFieldName] = value;
              }
            }
          });

          productDataArray.push(productDataForPost);
        }
      });

      if (productDataArray.length === 0) {
        throw new Error('No valid product links found in the first CSV');
      }

      setProgress(
        `Found ${productDataArray.length} products. Checking for stored product IDs...`
      );

      // Check for stored product IDs first
      const storedProductIds = loadProductIdsFromStorage();
      const productIdMap: Record<string, string> = storedProductIds || {};
      const productLinks = productDataArray.map((p) => p.product_link);
      const missingLinks = productLinks.filter((link) => !productIdMap[link]);

      let usedStoredIds = false;
      if (storedProductIds && Object.keys(storedProductIds).length > 0) {
        const matchedCount = productLinks.filter(
          (link) => productIdMap[link]
        ).length;
        if (matchedCount > 0) {
          usedStoredIds = true;
          setProgress(
            `Found ${matchedCount} stored product IDs. Creating ${missingLinks.length} new products...`
          );
        }
      }

      // Create products for missing links
      if (missingLinks.length > 0) {
        if (!usedStoredIds) {
          setProgress(
            `Sending ${missingLinks.length} products to API to get product IDs...`
          );
        }

        const batchSize = 10;
        const missingProductData = productDataArray.filter((p) =>
          missingLinks.includes(p.product_link)
        );

        for (let i = 0; i < missingProductData.length; i += batchSize) {
          const batch = missingProductData.slice(i, i + batchSize);
          setProgress(
            `Processing batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(missingProductData.length / batchSize)}...`
          );
          const lastSyncedAt = '2086-12-26T00:00:00.000Z';
          const promises = batch.map(async (productData) => {
            productData.lastSyncedAt = lastSyncedAt;
            try {
              const product = await createProduct(productData);
              if (product?.id) {
                productIdMap[productData.product_link] = String(product.id);
              }
            } catch (error) {
              console.error(
                `Error creating product for link ${productData.product_link}:`,
                error
              );
            }
          });

          await Promise.all(promises);
        }
      }

      // Store product IDs after successful product creation
      if (Object.keys(productIdMap).length > 0) {
        saveProductIdsToStorage(productIdMap);
      }

      const totalProductIds = Object.keys(productIdMap).length;
      const storedMessage = usedStoredIds
        ? ` (${productLinks.filter((link) => storedProductIds?.[link]).length} from previous attempt)`
        : '';
      setProgress(
        `Fetched ${totalProductIds} product IDs${storedMessage}. Creating curation...`
      );

      const allProductIds = Object.values(productIdMap)
        .filter((id) => id.trim() !== '')
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

      const curationRow = curationData[0] || {};

      const curationPayload: CreateCurationPayload = {
        myntraProductIds: allProductIds,
      };

      Object.keys(curationRow).forEach((key) => {
        const value = curationRow[key]?.trim();
        if (!value) return;

        const lowerKey = key.toLowerCase().trim();

        if (lowerKey === 'title') {
          curationPayload.title = value;
        } else if (lowerKey === 'curation description') {
          curationPayload.description = value;
        } else if (lowerKey === 'curation image') {
          curationPayload.curationImage = value;
        } else if (lowerKey === 'curation image title') {
          curationPayload.imageTitle = value;
        } else if (
          lowerKey === 'cover products' ||
          lowerKey === 'cover products '
        ) {
          curationPayload.thumbnailProducts = value
            .split(',')
            .map((id) => parseInt(id.trim(), 10))
            .filter((id) => !isNaN(id));
        } else if (lowerKey === 'related curation title') {
          const ids = value
            .split(',')
            .map((item) => parseInt(item.trim(), 10))
            .filter((id) => !isNaN(id));
          if (ids.length > 0) {
            curationPayload.relatedCurationIds = ids;
          }
        } else if (lowerKey === 'widget image') {
          curationPayload.widgetImage = value;
        } else if (lowerKey === 'curator') {
          const numValue = parseInt(value, 10);
          if (!isNaN(numValue)) {
            curationPayload.curator = numValue;
          } else {
            curationPayload.curatorName = value;
          }
        } else if (lowerKey === 'slug') {
          curationPayload.slug = value;
        }
      });

      const createdCuration = await createCuration(curationPayload);
      setCreatedCurationId(createdCuration.id);

      // Clear stored product IDs after successful curation creation
      clearProductIdsFromStorage();

      setProgress(
        `Successfully created curation! ID: ${createdCuration.id}. Found ${allProductIds.length} product IDs.`
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to process CSV files';
      setError(errorMessage);
      console.error(err);

      // If we have product IDs stored, inform the user they can retry
      const storedIds = loadProductIdsFromStorage();
      if (storedIds && Object.keys(storedIds).length > 0) {
        setProgress(
          (prev) =>
            `${prev}\nNote: Product IDs have been saved. Update your curation sheet and try again - stored product IDs will be reused.`
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const mapColumnToApiField = (columnName: string): string | null => {
    const lowerColumn = columnName.toLowerCase();
    const mapping: Record<string, string> = {
      name: 'name',
      price: 'price',
      discount_price: 'discount_price',
      brand: 'brand',
      color: 'color',
      category: 'category',
      subcategory: 'subcategory',
      gender: 'gender',
      marketplace: 'marketplace',
      image_url: 'image_url',
      imageurl: 'image_url',
      secondary_images: 'secondary_images',
      secondaryimages: 'secondary_images',
      available_sizes: 'available_sizes',
      availablesizes: 'available_sizes',
      description: 'description',
      rating: 'rating',
    };

    if (mapping[lowerColumn]) {
      return mapping[lowerColumn];
    }

    const normalized = lowerColumn.replace(/[\s_-]/g, '_');
    if (mapping[normalized]) {
      return mapping[normalized];
    }

    return columnName;
  };

  const findLinkColumn = (firstRow: ProductRow): string | null => {
    const possibleNames = [
      'productLink',
      'product_link',
      'link',
      'url',
      'productUrl',
      'product_url',
      'href',
    ];

    for (const key of Object.keys(firstRow)) {
      const lowerKey = key.toLowerCase();
      if (
        possibleNames.some((name) => lowerKey.includes(name)) ||
        firstRow[key]?.startsWith('http')
      ) {
        return key;
      }
    }

    for (const key of Object.keys(firstRow)) {
      if (firstRow[key]?.startsWith('http')) {
        return key;
      }
    }

    return null;
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>Create Curation from CSV</h1>
        <p className={styles.description}>
          Upload CSV files or paste Google Sheets/CSV URLs: one with product
          links and one with curation data. We&apos;ll create products from the
          links and then create a curation with those products.
        </p>

        <div className={styles.uploadSection}>
          <div className={styles.fileUpload}>
            <h3 className={styles.sectionTitle}>1. Product Links CSV</h3>
            <button
              className={styles.uploadButton}
              disabled={isProcessing || isLoadingUrl}
              onClick={() => productFileInputRef.current?.click()}
            >
              <RiUploadLine size={20} />
              {productFile ? productFile.name : 'Upload Product CSV'}
            </button>
            <input
              type="file"
              ref={productFileInputRef}
              className={styles.hiddenInput}
              accept=".csv"
              disabled={isProcessing || isLoadingUrl}
              onChange={handleProductFileChange}
            />
            <div className={styles.divider}>OR</div>
            <div className={styles.urlInputContainer}>
              <input
                type="text"
                className={styles.urlInput}
                placeholder="Paste Google Sheets or CSV URL here"
                value={productUrl}
                disabled={isProcessing || isLoadingUrl || !!productFile}
                onChange={(e) => setProductUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleProductUrlLoad();
                  }
                }}
              />
              <button
                className={styles.loadButton}
                disabled={
                  isProcessing ||
                  isLoadingUrl ||
                  !productUrl.trim() ||
                  !!productFile
                }
                onClick={handleProductUrlLoad}
              >
                {isLoadingUrl ? 'Loading...' : 'Load'}
              </button>
            </div>
            {productData.length > 0 && (
              <p className={styles.fileInfo}>
                {productData.length} rows loaded
              </p>
            )}
          </div>

          <div className={styles.fileUpload}>
            <h3 className={styles.sectionTitle}>2. Curation CSV</h3>
            <button
              className={styles.uploadButton}
              disabled={isProcessing || isLoadingUrl}
              onClick={() => curationFileInputRef.current?.click()}
            >
              <RiUploadLine size={20} />
              {curationFile ? curationFile.name : 'Upload Curation CSV'}
            </button>
            <input
              type="file"
              ref={curationFileInputRef}
              className={styles.hiddenInput}
              accept=".csv"
              disabled={isProcessing || isLoadingUrl}
              onChange={handleCurationFileChange}
            />
            <div className={styles.divider}>OR</div>
            <div className={styles.urlInputContainer}>
              <input
                type="text"
                className={styles.urlInput}
                placeholder="Paste Google Sheets or CSV URL here"
                value={curationUrl}
                disabled={isProcessing || isLoadingUrl || !!curationFile}
                onChange={(e) => setCurationUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCurationUrlLoad();
                  }
                }}
              />
              <button
                className={styles.loadButton}
                disabled={
                  isProcessing ||
                  isLoadingUrl ||
                  !curationUrl.trim() ||
                  !!curationFile
                }
                onClick={handleCurationUrlLoad}
              >
                {isLoadingUrl ? 'Loading...' : 'Load'}
              </button>
            </div>
            {curationData.length > 0 && (
              <p className={styles.fileInfo}>
                {curationData.length} rows loaded
              </p>
            )}
          </div>
        </div>

        <button
          className={styles.processButton}
          disabled={
            isProcessing ||
            isLoadingUrl ||
            productData.length === 0 ||
            curationData.length === 0
          }
          onClick={handleProcess}
        >
          {isProcessing ? 'Processing...' : 'Create Curation'}
        </button>

        {progress && (
          <div className={styles.progressContainer}>
            <p className={styles.progressText}>{progress}</p>
          </div>
        )}

        {error && (
          <div className={styles.errorContainer}>
            <p className={styles.errorText}>{error}</p>
          </div>
        )}

        {createdCurationId && (
          <div className={styles.successContainer}>
            <h3 className={styles.successTitle}>
              Curation Created Successfully!
            </h3>
            <div className={styles.curationLinkContainer}>
              <a
                href={`https://slayd.in/curations/v2/${createdCurationId}/`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.curationLink}
              >
                https://slayd.in/curations/v2/{createdCurationId}/
              </a>
              <button
                className={styles.copyButton}
                title="Copy link"
                onClick={() => {
                  const link = `https://slayd.in/curations/v2/${createdCurationId}/`;
                  navigator.clipboard.writeText(link);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? (
                  <RiCheckLine size={18} />
                ) : (
                  <RiFileCopyLine size={18} />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalCreateCurationPage;
